<template>
  <div class="sidenav-container" 
       @mouseenter="handleMouseEnter" 
       @mouseleave="handleMouseLeave">

    <aside class="side-nav" :class="{ 
      'collapsed': isCollapsed, 
      'expanded': !isCollapsed || isHoverExpanded,
      'open': isMobileOpen, 
      'hover-expanded': isHoverExpanded 
    }" v-if="isAuthenticated">
      <div class="side-nav-content">
        
        <nav class="side-nav-menu">
          <!-- Hauptnavigation -->
          <div class="nav-section">
            <span class="nav-section-title" v-if="!isCollapsed || isHoverExpanded">Hauptnavigation</span>
            
            <template v-if="(isSuperAdmin ? superAdminNav : firmNav).length === 0">
              <div class="nav-item-placeholder px-3">
                <small>Keine Navigationselemente gefunden</small>
              </div>
            </template>
            
            <template v-for="(navItem, index) in isSuperAdmin ? superAdminNav : firmNav" 
                   :key="index">
              <router-link 
                :to="navItem.path" 
                class="nav-item" 
                active-class="active"
                v-if="navItem && navItem.path"
              >
                <div class="nav-icon">
                  <i :class="navItem.icon"></i>
                </div>
                <span class="nav-label" v-if="!isCollapsed || isHoverExpanded">{{ navItem.title }}</span>
              </router-link>
            </template>
          </div>
          
          <!-- Benutzeraktionen für alle -->
          <div class="nav-section">
            <span class="nav-section-title" v-if="!isCollapsed || isHoverExpanded">Einstellungen</span>
            
            <template v-if="filteredUserNav.length === 0">
              <div class="nav-item-placeholder px-3">
                <small>Keine Benutzermenü-Elemente gefunden</small>
              </div>
            </template>
            
            <template v-for="(navItem, index) in filteredUserNav" :key="index">
              <router-link 
                :to="navItem.path" 
                class="nav-item" 
                active-class="active"
                v-if="navItem && navItem.path"
              >
                <div class="nav-icon">
                  <i :class="navItem.icon"></i>
                </div>
                <span class="nav-label" v-if="!isCollapsed || isHoverExpanded">{{ navItem.title }}</span>
              </router-link>
              
              <a 
                v-else-if="navItem && !navItem.path"
                @click.prevent="handleUserAction(navItem.id)" 
                class="nav-item"
                style="cursor: pointer;"
              >
                <div class="nav-icon">
                  <i :class="navItem.icon"></i>
                </div>
                <span class="nav-label" v-if="!isCollapsed || isHoverExpanded">{{ navItem.title }}</span>
              </a>
            </template>
          </div>
        </nav>
      </div>

      <div class="side-nav-footer">
        <div class="user-info">
          <div class="user-avatar">
            <img :src="userAvatar" alt="Benutzer" v-if="userAvatar">
            <span class="avatar-placeholder" v-else>{{ userInitials }}</span>
          </div>
          <div class="user-details" v-if="!isCollapsed || isHoverExpanded">
            <div class="user-name">{{ userName }}</div>
            <div class="user-role">{{ userRoleDisplay }}</div>
          </div>
        </div>
      </div>
    </aside>
    
    <div class="side-nav-overlay" :class="{ 'show': isMobileOpen }" @click="closeMobileMenu"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { 
  superAdminNavigation, 
  firmNavigation, 
  userNavigation,
  mainNavigation
} from '@/config/navigation';
import CompanyLogo from '@/components/ui/CompanyLogo.vue';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false
  }
});

// Emits explizit definieren
const emit = defineEmits(['toggle-sidebar', 'hover']);

const router = useRouter();
const authStore = useAuthStore();
const companyStore = useCompanyStore();

// Reaktive Datenzustände
const isCollapsed = ref(props.collapsed || false);
const isMobileOpen = ref(false);
const isMobileView = ref(false);
const isHoverExpanded = ref(false);
const mainNavItems = ref([]);
const companyLoaded = ref(false);
const companyInfo = ref(null);
const showDebug = ref(false);
const useCompanyLogo = ref(true);
const superAdminNav = ref(superAdminNavigation);
const firmNav = ref(firmNavigation);
const userNav = ref(userNavigation);

// Computed Properties
const isAuthenticated = computed(() => authStore.isAuthenticated);
const user = computed(() => authStore.user);
const isSuperAdmin = computed(() => authStore.isSuperAdmin);
const isCompanyAdmin = computed(() => authStore.isCompanyAdmin);
const isImpersonating = computed(() => authStore.isImpersonating);
const currentUser = computed(() => authStore.user);
const currentCompany = computed(() => companyStore.currentCompany);

const filteredUserNav = computed(() => {
  return userNav.value.filter(item => {
    // Sicherstellen, dass Item gültig ist
    if (!item) return false;
    
    if (item.hideForSuperAdmin && isSuperAdmin.value) {
      return false;
    }
    return true;
  });
});

const userName = computed(() => {
  return user.value && user.value.name ? user.value.name : 'Benutzer';
});

const userAvatar = computed(() => {
  return user.value && user.value.avatar ? user.value.avatar : null;
});

const userInitials = computed(() => {
  if (!user.value || !user.value.name) return '';
  
  const nameParts = user.value.name.split(' ');
  if (nameParts.length >= 2) {
    return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
  } else if (nameParts[0] && nameParts[0].length > 0) {
    return nameParts[0][0].toUpperCase();
  } else {
    return '';
  }
});

const userRoleDisplay = computed(() => {
  if (!user.value || !user.value.role) return 'Benutzer';
  
  if (user.value.role === 'super_admin') return 'Superadministrator';
  if (user.value.role === 'admin') return 'Administrator';
  return 'Benutzer';
});

const filteredMainNavItems = computed(() => {
  return mainNavItems.value.filter(item => {
    const u = currentUser.value;
    if (!u) return false;
    
    // Für Superadmins
    if (u.role === 'super_admin') {
      // Niemals firmOnly-Elemente für Superadmins anzeigen
      if (item.firmOnly) return false;
      
      // Zeige nur superAdminOnly-Items und allgemeine Items
      return item.superAdminOnly || 
            (!item.firmOnly && !item.adminOnly) || 
            item.id === 'companies';
    }
    
    // Für normale Benutzer
    
    // Superadmin-spezifische Items für normale Benutzer ausblenden
    if (item.superAdminOnly) return false;
    
    // Für Firmenbenutzer: firmOnly-Elemente anzeigen
    if (u.company_id) {
      // Für normale Admins: auch adminOnly-Elemente anzeigen
      if (item.adminOnly && u.role !== 'admin') return false;
      
      return true;
    }
    
    return false;
  });
});

const effectiveCompanyData = computed(() => {
  // Priorisierung der Datenquellen
  return companyInfo.value || 
          companyStore.currentCompany || 
          (currentUser.value && { id: currentUser.value.company_id, name: 'Meine Firma' });
});

const displayCompanyName = computed(() => {
  const company = effectiveCompanyData.value;
  
  if (!company) return 'Dashboard';
  
  // Verschiedene mögliche Datenstrukturen abdecken
  if (typeof company === 'object') {
    if (company.company && company.company.name) {
      return company.company.name;
    }
    return company.name || 'Meine Firma';
  }
  
  return 'Dashboard';
});

const debugInfo = computed(() => {
  return {
    storeCompany: companyStore.currentCompany,
    localCompany: companyInfo.value,
    companyLoaded: companyLoaded.value,
    effectiveCompany: effectiveCompanyData.value,
    getters: {
      getCurrentCompany: companyStore.currentCompany
    },
    user: currentUser.value
  }
});

// Methods
const toggleSidebar = () => {
  const newValue = !isCollapsed.value;
  isCollapsed.value = newValue;
  localStorage.setItem('sidebarCollapsed', newValue.toString());
  
  // Event an übergeordnete Komponente senden
  emit('toggle-sidebar', newValue);
  
  // Für responsives Design
  if (isMobileView.value) {
    isMobileOpen.value = !newValue;
  }
};

const closeMobileMenu = () => {
  isMobileOpen.value = false;
};

const handleResize = () => {
  isMobileView.value = window.innerWidth < 992;
  if (!isMobileView.value) {
    isMobileOpen.value = false;
  }
};

const handleUserAction = (id) => {
  // Aktionen basierend auf der ID ausführen
  switch (id) {
    case 'logout':
      logout();
      break;
    case 'exit-impersonation':
      authStore.stopImpersonating();
      router.push('/admin/dashboard');
      break;
    default:
      console.log(`Aktion: ${id}`);
  }
};

const logout = async () => {
  try {
    await authStore.logout();
    router.push('/login');
  } catch (error) {
    console.error('Fehler beim Ausloggen:', error);
  }
};

const handleMouseEnter = () => {
  if (isCollapsed.value && !isMobileView.value) {
    isHoverExpanded.value = true;
    emit('hover', true);
  }
};

const handleMouseLeave = () => {
  if (isCollapsed.value && isHoverExpanded.value) {
    isHoverExpanded.value = false;
    emit('hover', false);
  }
};

const getCompanyColor = (name) => {
  if (!name) return '#3490dc';
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF;
    color += ('00' + value.toString(16)).substr(-2);
  }
  
  return color;
};

// Initialisiere die mainNavItems mit den importierten mainNavigation-Daten
mainNavItems.value = mainNavigation;

// Lifecycle hooks
onMounted(() => {
  // Gespeicherten Zustand des Menüs wiederherstellen
  const savedState = localStorage.getItem('sidebarCollapsed');
  if (savedState !== null) {
    isCollapsed.value = savedState === 'true';
  }
  
  // Auf kleineren Geräten automatisch einklappen
  handleResize();
  window.addEventListener('resize', handleResize);
  
  // CSS-Variable zur Aktivierung der Animation nach dem ersten Rendering
  nextTick(() => {
    document.documentElement.style.setProperty('--sidenav-animation-enabled', 'true');
  });
  
  // Debug-Modus aus lokaler Speicherung wiederherstellen
  const debugMode = localStorage.getItem('sideNavDebug');
  showDebug.value = debugMode === 'true';
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

// Watch
watch(() => companyStore.currentCompany, (newCompany) => {
  if (newCompany && newCompany.id && (!companyInfo.value || companyInfo.value.id !== newCompany.id)) {
    console.log('Firma im Store hat sich geändert:', newCompany.name);
    companyInfo.value = newCompany;
    companyLoaded.value = true;
  }
}, { deep: true });

watch(showDebug, (newValue) => {
  localStorage.setItem('sideNavDebug', newValue);
});

// Watch für die collapsed Prop hinzufügen
watch(() => props.collapsed, (newValue) => {
  isCollapsed.value = newValue;
}, { immediate: true });
</script>

<style lang="scss" scoped>
:root {
  --sidenav-width-expanded: 260px;
  --sidenav-width-collapsed: 70px;
  --sidenav-animation-duration: 0.3s;
  --header-height: 70px;
}

.sidenav-container {
  position: relative;
  height: 100%;
  
  .side-nav {
    position: fixed;
    top: var(--header-height); /* Unter dem Header starten */
    left: 0;
    bottom: 0;
    width: var(--sidenav-width-expanded);
    background: var(--sidebar-bg, #fff);
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.05);
    transition: width var(--sidenav-animation-duration) cubic-bezier(0.22, 1, 0.36, 1);
    overflow-x: hidden;
    z-index: 1020; /* Unter Header (1030) */
    
    &.collapsed {
      width: var(--sidenav-width-collapsed);
      
      // Wenn nicht im Hover-Zustand
      &:not(.hover-expanded) {
        .nav-label,
        .nav-section-title {
          opacity: 0;
          transform: translateX(-20px);
          visibility: hidden;
        }
      }
    }
    
    // Hover-expanded für Hover-Effekt
    &.hover-expanded {
      width: var(--sidenav-width-expanded);
      
      .nav-label,
      .nav-section-title {
        opacity: 1;
        transform: translateX(0);
        transition-delay: 0.1s;
        visibility: visible;
      }
    }
    
    // Dunkles Design für Dark Mode
    .dark-mode & {
      background: var(--dark-bg-secondary);
      box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
    }
  }
  
  .side-nav-content {
    padding-top: 15px; /* Abstand oben hinzufügen */
  }
}

// CSS für die Navigation
.nav-section {
  
  .nav-section-title {
    font-size: 0.75rem;
    text-transform: uppercase;
    color: #6c757d;
    padding: 0.5rem 1.5rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
    transition: all var(--sidenav-animation-duration) ease;
  }
  
  .nav-item {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.5rem;
    color: var(--text-color, #212529);
    text-decoration: none;
    border-radius: 0.25rem;
    margin: 0.125rem 0.75rem;
    transition: background-color 0.2s ease, color 0.2s ease;
    overflow: hidden;
    white-space: nowrap;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
    
    &.active {
      background-color: var(--primary-color);
      color: white;
      
      .nav-icon {
        color: white;
      }
    }
    
    .nav-icon {
      color: var(--primary-color);
      font-size: 1.2rem;
      width: 1.5rem;
      text-align: center;
      margin-right: 1rem;
      flex-shrink: 0;
    }
    
    .nav-label {
      font-weight: 500;
      transition: opacity var(--sidenav-animation-duration) ease,
                  transform var(--sidenav-animation-duration) ease;
    }
  }
}

.side-nav-footer {
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  border-top: 1px solid #eee;
  background: var(--sidebar-bg, #fff);
  height: auto;
  min-height: 4rem;
  z-index: 10;
  display: block !important;
  
  .dark-mode & {
    border-top: 1px solid var(--dark-border-color, #2d3748);
    background: var(--dark-bg-secondary, #1f2937);
  }
  
  .user-info {
    display: flex;
    align-items: center;
    
    .user-avatar {
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 50%;
      overflow: hidden;
      margin-right: 0.75rem;
      flex-shrink: 0;
      background-color: var(--primary-color);
      display: flex;
      align-items: center;
      justify-content: center;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      
      .avatar-placeholder {
        color: white;
        font-weight: 600;
        font-size: 1rem;
      }
    }
    
    .user-details {
      overflow: hidden;
      
      .user-name {
        font-weight: 600;
        font-size: 0.9rem;
        margin-bottom: 0.125rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .user-role {
        font-size: 0.8rem;
        color: #6c757d;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}

// Debug-Panel
.debug-panel {
  position: fixed;
  top: 10px;
  right: 10px;
  background: rgba(0,0,0,0.8);
  color: #fff;
  padding: 10px;
  border-radius: 4px;
  max-width: 400px;
  max-height: 500px;
  overflow: auto;
  z-index: 9999;
  font-size: 12px;
  
  pre {
    margin: 0;
    color: #0f0;
    overflow: auto;
    max-height: 300px;
  }
  
  .debug-controls {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
  }
}
</style>
